import { useState, useEffect } from "react";
import { Download, Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, Info, ImageOff } from "lucide-react";
import { photoThumb, photoThumbBackend, photoFull } from "@/lib/photoUrl";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useOrderProductList, ALL } from "./useOrderProducts";
import { packingSummary, packingSummaryCompact, PACKING_PREVIEW, trimNum, priceDelta } from "./orderProductFields";

/**
 * 导入订单产品资料 — reusable picker over 合同订单产品一览表.
 *
 * Props
 *  • open / onOpenChange   dialog control
 *  • defaultSearch         prefilled keyword (usually the current 产品编号)
 *  • onPick(record)        called on 导入 / 确认导入 — caller maps the fields
 *  • title / subtitle      optional overrides
 *  • previewFields(t)      right-panel field list (defaults to 装箱数据)
 */
export function OrderProductPicker({
  open, onOpenChange, defaultSearch = "", onPick,
  title, subtitle, previewFields = PACKING_PREVIEW,
  allowPriceImport = false, currentPrice = null, lowestId = null,
}) {
  const { t } = useLanguage();
  const L = useOrderProductList({ enabled: open, defaultSearch });
  const [selected, setSelected] = useState(null);
  const [withPrice, setWithPrice] = useState(false);
  const pick = (row) => onPick(row, { withPrice });

  useEffect(() => {
    if (!open) return;
    L.setSearch(defaultSearch);
    L.setContractNo(ALL);
    L.setCustomer(ALL);
    L.setPage(1);
    setSelected(null);
    setWithPrice(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultSearch]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1280px] max-h-[94vh] overflow-hidden flex flex-col"
        data-testid="co-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download size={16} className="text-brand" /> {title || t("co.dialogTitle")}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{subtitle || t("co.dialogSubtitle")}</p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px_180px_auto] gap-3 items-end rounded-md border border-border/70 bg-secondary/25 p-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">{t("co.searchLabel")}</Label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={L.search} onChange={(e) => L.setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && L.runSearch()}
                placeholder={t("co.searchPlaceholder")} className="pl-8 h-9"
                data-testid="co-search-input" />
            </div>
          </div>
          {/* 2026-06 · 合同号 / 客户 可手动输入，输入时按已有选项自动提示 */}
          <SuggestField
            label={t("co.contractNo")} placeholder={t("co.all")}
            value={L.contractNo === ALL ? "" : L.contractNo}
            options={L.filters.contracts} listId="co-contract-options"
            testId="co-contract-select"
            onChange={(v) => { L.setContractNo(v || ALL); L.setPage(1); }}
            onCommit={L.runSearch}
          />
          <SuggestField
            label={t("co.customer")} placeholder={t("co.all")}
            value={L.customer === ALL ? "" : L.customer}
            options={L.filters.customers} listId="co-customer-options"
            testId="co-customer-select"
            onChange={(v) => { L.setCustomer(v || ALL); L.setPage(1); }}
            onCommit={L.runSearch}
          />
          <SuggestField
            label={t("co.factory")} placeholder={t("co.all")}
            value={L.factory === ALL ? "" : L.factory}
            options={L.filters.factories || []} listId="co-factory-options"
            testId="co-factory-select"
            onChange={(v) => { L.setFactory(v || ALL); L.setPage(1); }}
            onCommit={L.runSearch}
          />
          <div className="flex gap-2">
            <Button onClick={L.runSearch} className="h-9 bg-brand hover:bg-brand-hover" data-testid="co-search-button">
              {t("co.search")}
            </Button>
            <Button variant="outline" onClick={L.reset} className="h-9" data-testid="co-reset-button">
              {t("co.reset")}
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 mt-1">
          <div className="rounded-md border border-border/70 flex flex-col min-h-0">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60">
              <span className="text-xs text-muted-foreground" data-testid="co-total">
                {t("co.recordCount").replace("{n}", L.total)}
              </span>
              <Button variant="ghost" size="sm" className="h-7 ml-auto px-2 text-xs"
                onClick={() => L.fetchRows()} disabled={L.loading} data-testid="co-refresh">
                {L.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {t("co.refresh")}
              </Button>
            </div>
            <div className="flex-1 overflow-auto" data-testid="co-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-secondary/70 backdrop-blur">
                  <tr className="text-left text-[11px] uppercase text-muted-foreground">
                    <th className="w-8" />
                    <th className="px-2 py-2">{t("co.colName")}</th>
                    <th className="px-2 py-2 w-[92px]">{t("co.colItem")}</th>
                    <th className="px-2 py-2 w-[84px]">{t("co.colContract")}</th>
                    <th className="px-2 py-2 w-[108px] text-right">{t("co.colPurchasePrice")}</th>
                    <th className="px-2 py-2 w-[76px] text-right">{t("co.gtPrice")}</th>
                    <th className="px-2 py-2 w-[76px] text-right">{t("co.exportPrice")}</th>
                    <th className="px-2 py-2 w-[56px] text-right">{t("co.colQty")}</th>
                    <th className="px-2 py-2 w-[190px]">{t("co.colPacking")}</th>
                    <th className="px-2 py-2 w-[112px] text-right">{t("co.colActions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {!L.loading && L.items.length === 0 && (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground" data-testid="co-empty">
                      {t("co.empty")}
                    </td></tr>
                  )}
                  {L.items.map((row) => (
                    <tr key={row.id}
                      className={`cursor-pointer hover:bg-secondary/40 ${selected?.id === row.id ? "bg-brand/5" : ""}`}
                      onClick={() => setSelected(row)} data-testid={`co-row-${row.id}`}>
                      <td className="pl-3">
                        <input type="radio" checked={selected?.id === row.id} onChange={() => setSelected(row)}
                          data-testid={`co-radio-${row.id}`} />
                      </td>
                      <td className="px-2 py-2 max-w-[250px]">
                        <p className="truncate">{row.name_cn || row.name_en || "-"}</p>
                        {row.name_cn && row.name_en && (
                          <p className="text-xs text-muted-foreground line-clamp-2"
                            data-testid={`co-name-en-${row.id}`}>{row.name_en}</p>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono">{row.display_item || row.item_number || "-"}</td>
                      <td className="px-2 py-2 font-mono">{row.contract_no || "-"}</td>
                      <td className="px-2 py-2 text-right font-mono" data-testid={`co-price-${row.id}`}>
                        {lowestId === row.id && (
                          <span className="mr-1 px-1 py-px rounded-sm bg-emerald-100 text-emerald-700 text-[9px] font-semibold"
                            data-testid={`co-lowest-badge-${row.id}`}>{t("co.lowestBadge")}</span>
                        )}
                        {row.purchase_price ? `¥${trimNum(row.purchase_price)}` : "-"}
                        <PriceTrend historic={row.purchase_price} current={currentPrice} />
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-muted-foreground" data-testid={`co-gt-price-${row.id}`}>
                        {row.gt_price ? `C$${trimNum(row.gt_price)}` : "-"}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-muted-foreground" data-testid={`co-export-price-${row.id}`}>
                        {row.export_price ? `US$${trimNum(row.export_price)}` : "-"}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-muted-foreground">
                        {row.qty ? trimNum(row.qty) : "-"}
                      </td>
                      <td className="px-2 py-2 w-[190px]">
                        <span className="font-mono text-[11px] leading-4 text-muted-foreground line-clamp-3"
                          title={packingSummary(row, t)}>
                          {packingSummaryCompact(row)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] mr-1"
                          onClick={(e) => { e.stopPropagation(); setSelected(row); }}
                          data-testid={`co-preview-${row.id}`}>
                          {t("co.preview")}
                        </Button>
                        <Button size="sm" className="h-6 px-2 text-[10px] bg-brand hover:bg-brand-hover"
                          onClick={(e) => { e.stopPropagation(); pick(row); }}
                          data-testid={`co-import-${row.id}`}>
                          {t("co.import")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-center gap-2 px-3 py-2 border-t border-border/60 text-xs">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={L.page <= 1}
                onClick={() => L.setPage((p) => p - 1)} data-testid="co-prev-page"><ChevronLeft size={13} /></Button>
              <span className="font-mono" data-testid="co-page-info">{L.page} / {L.lastPage}</span>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={L.page >= L.lastPage}
                onClick={() => L.setPage((p) => p + 1)} data-testid="co-next-page"><ChevronRight size={13} /></Button>
              <Select value={String(L.pageSize)} onValueChange={(v) => { L.setPageSize(Number(v)); L.setPage(1); }}>
                <SelectTrigger className="h-7 w-[110px] ml-2" data-testid="co-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[20, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} {t("co.perPage")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border border-border/70 p-2.5 overflow-y-auto" data-testid="co-preview-panel">
            <h3 className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              <span className="h-3 w-0.5 bg-brand rounded-full" aria-hidden />
              {t("co.selectedTitle")}
            </h3>
            {!selected && <p className="text-xs text-muted-foreground">{t("co.selectHint")}</p>}
            {selected && (
              /* 2026-06-26 · 紧凑版详细资料：图片与名称并排、元信息两列、价格 2×2 指标卡、装箱三列 */
              <div className="space-y-2">
                <div className="flex gap-2.5">
                  {/* 2026-06 · 产品图片 — 订单产品不带 FM 图片容器,按产品编号从产品资料库带出 */}
                  <a href={selected.product_photo_url ? photoFull(selected.product_photo_url) : undefined}
                    target="_blank" rel="noreferrer"
                    className="w-24 h-24 shrink-0 rounded-md border border-border/70 bg-white overflow-hidden flex items-center justify-center"
                    data-testid="co-preview-photo-box">
                    {selected.product_photo_url ? (
                      <img src={photoThumb(selected.product_photo_url, "md")}
                        alt={selected.display_item || selected.item_number || ""}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const i = e.currentTarget;
                          if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(selected.product_photo_url, "md"); }
                        }}
                        data-testid="co-preview-photo" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        <ImageOff size={18} />
                        <span className="text-[9px]" data-testid="co-preview-photo-empty">{t("co.noPhoto")}</span>
                      </div>
                    )}
                  </a>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold leading-tight">{selected.name_cn || selected.name_en || "-"}</p>
                    {selected.name_cn && selected.name_en && (
                      <p className="text-[11px] leading-snug text-muted-foreground line-clamp-3"
                        data-testid="co-preview-name-en">{selected.name_en}</p>
                    )}
                    <p className="mt-1 font-mono text-xs">{selected.display_item || selected.item_number || "-"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-3 rounded-md bg-secondary/30 p-2">
                  <KV k={t("co.colContract")} v={selected.contract_no} mono />
                  <KV k={t("co.contractDate")} v={selected.contract_date} mono />
                  <KV k={t("co.customer")} v={selected.customer_name} />
                  <KV k={t("co.factory")} v={selected.factory} />
                </div>

                <div className="grid grid-cols-2 gap-1.5" data-testid="co-preview-price">
                  <Stat label={t("co.colPurchasePrice")}
                    value={selected.purchase_price ? `¥${trimNum(selected.purchase_price)}` : "-"}
                    extra={currentPrice ? (
                      <span className="text-[10px] text-muted-foreground">
                        {t("co.vsCurrent")} ¥{trimNum(currentPrice)}
                        <PriceTrend historic={selected.purchase_price} current={currentPrice} />
                      </span>
                    ) : null} />
                  <Stat label={t("co.colQty")} value={selected.qty ? trimNum(selected.qty) : "-"} />
                  <Stat label={t("co.gtPrice")} testId="co-preview-gt-price"
                    value={selected.gt_price ? `C$${trimNum(selected.gt_price)}` : "-"} />
                  <Stat label={t("co.exportPrice")} testId="co-preview-export-price"
                    value={selected.export_price ? `US$${trimNum(selected.export_price)}` : "-"} />
                </div>

                <div>
                  <p className="text-[11px] font-semibold text-brand mb-1">{t("co.packingWillImport")}</p>
                  <div className="grid grid-cols-3 gap-1.5" data-testid="co-preview-packing">
                    {previewFields(t).map((f) => (
                      <div key={f.key} className="rounded-md bg-secondary/40 px-2 py-1">
                        <p className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{f.label}</p>
                        <p className="font-mono text-xs">{trimNum(selected[f.key])}{f.unit ? <span className="text-[9px] text-muted-foreground ml-0.5">{f.unit}</span> : null}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="flex items-center gap-1 text-[10px] leading-tight text-muted-foreground">
                  <Info size={11} className="shrink-0" /> {t("co.overwriteHint")}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="items-center">
          {allowPriceImport && (
            <label className="flex items-center gap-2 mr-auto text-xs cursor-pointer">
              <Checkbox checked={withPrice} onCheckedChange={(v) => setWithPrice(Boolean(v))}
                data-testid="co-with-price-checkbox" />
              {t("co.alsoImportPrice")}
            </label>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="co-cancel">
            {t("common.cancel")}
          </Button>
          <Button disabled={!selected} onClick={() => pick(selected)}
            className="bg-brand hover:bg-brand-hover" data-testid="co-confirm-import">
            {t("co.confirmImport")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 涨跌对比小标签 — 当前价 vs 历史采购价。红=涨，绿=跌。 */
const PriceTrend = ({ historic, current }) => {
  const d = priceDelta(historic, current);
  if (!d || d.dir === "flat") return null;
  const up = d.dir === "up";
  return (
    <span className={`ml-1 text-[10px] font-semibold ${up ? "text-red-600" : "text-emerald-600"}`}>
      {up ? "▲" : "▼"}{Math.abs(d.pct).toFixed(1)}%
    </span>
  );
};

/** Typeable filter with native datalist autocomplete over the known options. */
const SuggestField = ({ label, value, options, listId, onChange, onCommit, placeholder, testId }) => (
  <div className="space-y-1">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    <Input
      value={value}
      list={listId}
      placeholder={placeholder}
      className="h-9"
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && onCommit()}
    />
    <datalist id={listId}>
      {options.slice(0, 800).map((o) => <option key={o} value={o} />)}
    </datalist>
  </div>
);

/** 紧凑键值行（标签左 · 值右），用于详细资料的两列元信息网格。 */
const KV = ({ k, v, mono = false }) => (
  <div className="flex items-baseline justify-between gap-2 min-w-0 leading-5">
    <span className="text-[10px] text-muted-foreground shrink-0">{k}</span>
    <span className={`text-[11px] truncate ${mono ? "font-mono" : ""}`} title={v || "-"}>{v || "-"}</span>
  </div>
);

/** 指标卡：小标签 + 大数字（+ 可选副行）。 */
const Stat = ({ label, value, extra = null, testId }) => (
  <div className="rounded-md bg-secondary/40 px-2 py-1.5" data-testid={testId}>
    <p className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{label}</p>
    <p className="font-mono text-sm font-bold leading-tight">{value}</p>
    {extra}
  </div>
);

